import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CreditCard, Lock, ArrowLeft, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const PaymentModal = ({ onSuccess, onCancel }) => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast({
        title: "Missing Information",
        description: "Please enter your name and email.",
        variant: "destructive"
      });
      return;
    }
    
    setIsProcessing(true);

    try {
      // Create Stripe Checkout Session
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/payment/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: formData.email,
          user_name: formData.name
        })
      });
      
      if (!response.ok) {
        throw new Error('Payment initialization failed');
      }
      
      const data = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
      
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: "Payment Failed",
        description: "Unable to initialize payment. Please try again.",
        variant: "destructive"
      });
      setIsProcessing(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-md w-full p-8 shadow-2xl relative">
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          disabled={isProcessing}
        >
          ✕
        </button>

        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-pink-400 to-purple-400 rounded-full mb-4">
            <CreditCard className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Secure Payment
          </h2>
          <p className="text-gray-600">Complete your purchase for £25</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <Input
              type="text"
              placeholder="John Smith"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              disabled={isProcessing}
              className="input-focus"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <Input
              type="email"
              placeholder="john@example.com"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              disabled={isProcessing}
              className="input-focus"
            />
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-600 bg-green-50 p-3 rounded-lg">
            <Lock className="w-4 h-4 text-green-600" />
            <span>Secure payment powered by Stripe</span>
          </div>

          <div className="space-y-3 pt-2">
            <Button
              type="submit"
              disabled={isProcessing}
              className="btn-primary w-full text-white py-6 text-lg font-semibold rounded-full border-0"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Redirecting to Stripe...
                </>
              ) : (
                <>Continue to Payment</>
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isProcessing}
              className="w-full flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Cancel
            </Button>
          </div>
        </form>

        <p className="text-xs text-center text-gray-500 mt-4">
          You'll be redirected to Stripe's secure checkout page
        </p>
      </div>
    </div>
  );
};

export default PaymentModal;
